import { useEffect, useRef, useCallback, useState } from "react";
import { io } from "socket.io-client";
import { getSocketUrl, getSocketOptions } from "../utils/socketConfig";

const HEARTBEAT_INTERVAL = 2 * 60 * 1000; // 2 minutes

/**
 * Hook that manages the attendance socket lifecycle:
 * - Auto check-in on mount (with geolocation)
 * - Heartbeat every 2 minutes
 * - Manual check-out
 *
 * @param {object} options
 * @param {string} options.token - JWT access token
 * @param {boolean} options.enabled - whether to activate the hook
 * @param {function} options.onCheckIn - callback after successful check-in
 * @param {function} options.onCheckOut - callback after successful check-out
 * @param {function} options.onError - callback on error
 */
export function useAttendanceSocket({
  token,
  enabled = true,
  onCheckIn,
  onCheckOut,
  onError,
} = {}) {
  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const checkedInRef = useRef(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Get user's geolocation
  const getLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ lat: null, lng: null });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        () => {
          // If user denies or error, proceed without location
          resolve({ lat: null, lng: null });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  }, []);

  // Check in
  const performCheckIn = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket?.connected || checkedInRef.current) return;

    try {
      const { lat, lng } = await getLocation();
      socket.emit("attendance:checkin", { lat, lng }, (response) => {
        if (response?.ok) {
          checkedInRef.current = true;
          setIsCheckedIn(true);
          setValidationError(null);
          onCheckIn?.(response);
        } else if (response?.error) {
          if (response.code === "ATTENDANCE_VALIDATION_FAILED") {
            setValidationError(response.error);
          }
          onError?.(response.error);
        }
      });
    } catch (err) {
      onError?.(err.message || "Check-in failed");
    }
  }, [getLocation, onCheckIn, onError]);

  // Check out (called externally)
  const checkOut = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      onError?.("Not connected to server");
      return;
    }

    try {
      const { lat, lng } = await getLocation();
      return new Promise((resolve) => {
        socket.emit("attendance:checkout", { lat, lng }, (response) => {
          if (response?.ok) {
            checkedInRef.current = false;
            setIsCheckedIn(false);
            setValidationError(null);
            onCheckOut?.(response);
            resolve(response);
          } else {
            if (response?.code === "ATTENDANCE_VALIDATION_FAILED") {
              setValidationError(response.error);
            }
            onError?.(response?.error || "Check-out failed");
            resolve(null);
          }
        });
      });
    } catch (err) {
      onError?.(err.message || "Check-out failed");
      return null;
    }
  }, [getLocation, onCheckOut, onError]);

  // Start heartbeat
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(async () => {
      const socket = socketRef.current;
      if (socket?.connected) {
        const { lat, lng } = await getLocation();
        socket.emit("attendance:heartbeat", { lat, lng }, () => {});
      }
    }, HEARTBEAT_INTERVAL);
  }, [getLocation]);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    const socket = io(getSocketUrl(), getSocketOptions(token));
    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      // Auto check-in on connect
      performCheckIn();
      startHeartbeat();
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
      stopHeartbeat();
    });

    socket.on("connect_error", () => {
      setIsConnected(false);
    });

    return () => {
      stopHeartbeat();
      socket.disconnect();
      socketRef.current = null;
      checkedInRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token]);

  return {
    isCheckedIn,
    isConnected,
    validationError,
    checkOut,
  };
}
